import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useProgram, useDeleteProgram, useUpdateProgram } from '../hooks/usePrograms';
import { useDances } from '../hooks/useDances';
import { useAuth } from '../contexts/AuthContext';

export function ProgramDetailPage() {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: program, isLoading, isError } = useProgram(programId);
  const { data: allDances } = useDances('ALL');
  const deleteProgram = useDeleteProgram();
  const updateProgram = useUpdateProgram();
  const [printView, setPrintView] = useState(false);

  if (isLoading) return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: 'var(--spacing-xl)' }}>
      <div className="skeleton skeleton-badge" style={{ width: '8rem', marginBottom: 'var(--spacing-md)' }}></div>
      <div className="skeleton-card">
        <div className="skeleton skeleton-title"></div>
        <div className="skeleton skeleton-text" style={{ width: '50%' }}></div>
        <div style={{ marginTop: 'var(--spacing-lg)' }}>
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton" style={{ height: '3rem', marginBottom: 'var(--spacing-sm)' }}></div>
          ))}
        </div>
      </div>
    </div>
  );
  if (isError || !program) return <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>Program not found.</div>;

  const canEdit = user && program.userId === user.userId;
  const shareUrl = `${window.location.origin}/programs/public/${program.publicId}`;

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${program.title}"? This cannot be undone.`)) return;
    try {
      await deleteProgram.mutateAsync(program.programId);
      navigate('/programs');
    } catch (err) {
      alert(`Failed to delete program: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(shareUrl);
    alert('Share link copied to clipboard!');
  };

  const handleTogglePublic = async () => {
    try {
      await updateProgram.mutateAsync({
        programId: program.programId,
        data: { isPublic: !program.isPublic }
      });
    } catch (err) {
      alert(`Failed to update program: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: 'var(--spacing-xl)' }}>
      <button onClick={() => navigate('/programs')} className="btn btn-secondary" style={{ marginBottom: 'var(--spacing-md)' }}>
        ← My Programs
      </button>

      <div className="card" style={{ backgroundColor: 'white', padding: 'var(--spacing-xl)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 'var(--spacing-lg)' }}>
          <div>
            <h1 style={{ margin: 0 }}>{program.title}</h1>
            {program.description && <p style={{ margin: 'var(--spacing-sm) 0 0', color: '#666' }}>{program.description}</p>}
            <div style={{ marginTop: 'var(--spacing-sm)', fontSize: '0.875rem', color: '#888' }}>
              by {program.username} · {program.dances.length} dance{program.dances.length !== 1 ? 's' : ''}
              {program.isPublic && <span style={{ marginLeft: '0.5rem', color: '#28a745' }}>• Public</span>}
            </div>
          </div>

          {canEdit && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={handleShare} className="btn" style={{ backgroundColor: '#17a2b8', color: 'white' }}>
                Share
              </button>
              <button onClick={handleTogglePublic} className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                {program.isPublic ? 'Make Private' : 'Make Public'}
              </button>
              <button onClick={() => navigate(`/programs/${program.programId}/edit`)} className="btn btn-warning">
                Edit
              </button>
              <button onClick={handleDelete} className="btn btn-danger">
                Delete
              </button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }} className="no-print">
          <button onClick={() => setPrintView(!printView)} className="btn btn-secondary">
            {printView ? 'Compact View' : 'Print View'}
          </button>
          <button onClick={() => window.print()} className="btn btn-primary">
            Print
          </button>
        </div>

        {program.dances.length === 0 ? (
          <p style={{ color: '#666', fontStyle: 'italic' }}>No dances in this program yet.</p>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {program.dances.map((slot, index) => {
              const dance = allDances?.find(d => d.danceId === slot.danceId);
              return (
                <div key={slot.danceId} style={{
                  padding: 'var(--spacing-md)',
                  border: '1px solid #e0e0e0',
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#999', minWidth: '2rem' }}>{index + 1}</span>
                    <div style={{ flex: 1 }}>
                      {dance ? (
                        <Link to={`/dances/${dance.danceId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          <strong>{dance.title}</strong>
                          {dance.choreographer && <span style={{ color: '#666', fontSize: '0.875rem' }}> by {dance.choreographer}</span>}
                        </Link>
                      ) : (
                        <strong style={{ color: '#999' }}>Dance not found ({slot.danceId})</strong>
                      )}
                      {dance && (
                        <div style={{ marginTop: '0.25rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          {dance.difficulty && <span className="badge badge-difficulty" style={{ fontSize: '0.75rem' }}>{dance.difficulty}</span>}
                          {dance.energyLevel && (
                            <span style={{
                              padding: '0.1rem 0.4rem',
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              backgroundColor: dance.energyLevel === 'high' ? '#dc3545' : dance.energyLevel === 'medium' ? '#fd7e14' : '#28a745',
                              color: 'white'
                            }}>{dance.energyLevel}</span>
                          )}
                          {dance.programRole && (
                            <span style={{ padding: '0.1rem 0.4rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: '#6f42c1', color: 'white' }}>{dance.programRole}</span>
                          )}
                        </div>
                      )}
                      {printView && dance && (
                        <div className="program-dance-notation" style={{ marginTop: 'var(--spacing-md)' }}>
                          <div className="dance-section dance-section-a1">
                            <div className="dance-section-header">A1</div>
                            <p style={{ margin: 0, fontSize: '0.95rem' }}>{dance.a1}</p>
                          </div>
                          <div className="dance-section dance-section-a2">
                            <div className="dance-section-header">A2</div>
                            <p style={{ margin: 0, fontSize: '0.95rem' }}>{dance.a2}</p>
                          </div>
                          <div className="dance-section dance-section-b1">
                            <div className="dance-section-header">B1</div>
                            <p style={{ margin: 0, fontSize: '0.95rem' }}>{dance.b1}</p>
                          </div>
                          <div className="dance-section dance-section-b2">
                            <div className="dance-section-header">B2</div>
                            <p style={{ margin: 0, fontSize: '0.95rem' }}>{dance.b2}</p>
                          </div>
                          {dance.callerNotes && (
                            <div style={{ marginTop: 'var(--spacing-sm)', fontSize: '0.875rem', color: '#856404', fontStyle: 'italic' }}>
                              Caller notes: {dance.callerNotes}
                            </div>
                          )}
                        </div>
                      )}
                      {slot.callerNotes && (
                        <div style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#856404', fontStyle: 'italic' }}>
                          Note: {slot.callerNotes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {canEdit && program.isPublic && (
          <div style={{ marginTop: 'var(--spacing-xl)', padding: 'var(--spacing-md)', backgroundColor: '#d1ecf1', borderRadius: 'var(--radius-sm)', borderLeft: '4px solid #17a2b8', fontSize: '0.875rem' }}>
            <strong>Public link:</strong> <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{shareUrl}</span>
          </div>
        )}
      </div>
    </div>
  );
}
